import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/common/modal';
import { Button } from '@/components/form/button';
import { Input } from '@/components/form/input';
import { updateBatch } from '@/features/single-measurement/services/batches';

import { IBatch, State } from '../../types';
import { shouldRenderInput } from '../../utils/conditional-input-render';
import { getBatchCreationValidation } from '../../validations/batch-analysis-creation';
import { ViabilityConcentrationFields } from '../create-analysis-wizard/steps/create-batch-step';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  /** analysis data */
  data: IBatch;

  onSuccess: () => void;
}

export const EditAnalysisModal: React.FC<IProps> = ({
  data,
  isOpen,
  toggle,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // update batch mutation
  const updateBatchMutation = useMutation({
    mutationFn: ({ id, batch }: { id: string; batch: Partial<IBatch> }) =>
      updateBatch(id, { ...batch }),
    onSuccess: () => {
      toggle();
      toast.success(t('Changes has been made, will be updated in shortly!'), {
        duration: 4000,
      });
      onSuccess();
    },
  });

  // based on the analysis method we switch the validation schema
  // only few input fields are getting removed from the UI based on the analysis method
  // if the validation schema become complex, consider creating two seperate validations for each type
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ViabilityConcentrationFields>({
    resolver: yupResolver(getBatchCreationValidation(data.method)),
  });

  const handleOnSubmit = handleSubmit((formData) => {
    const batch: Partial<IBatch> = { ...formData, state: State.UploadCompleted };
    updateBatchMutation.mutate({ id: data.id, batch });
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} title="editMeasurements.modalTitle">
      <form>
        <div className="flex flex-col gap-2 mt-8">
          <Input
            name="name"
            errors={errors}
            className="ml-28"
            register={register}
            defaultValue={data.name}
            label={t('editMeasurements.title')}
            labelStyleClass="w-full text-gray-700"
          />

          <Input
            errors={errors}
            className="ml-28"
            name="description"
            register={register}
            defaultValue={data.description}
            label={t('editMeasurements.comment')}
            labelStyleClass="w-full text-gray-700"
          />

          <h2 className="mt-3 font-bold">{t('editMeasurements.dilutionSample')}</h2>

          <Input<ViabilityConcentrationFields>
            min="0"
            lang="de-DE"
            type="number"
            errors={errors}
            register={register}
            name="dilutionSample1"
            className="ml-28 text-center"
            label={t('measurements.sample')}
            defaultValue={data.dilutionSample1}
            labelStyleClass="w-full text-gray-700"
          />

          <Input<ViabilityConcentrationFields>
            min="0"
            lang="de-DE"
            type="number"
            errors={errors}
            register={register}
            name="dilutionSample2"
            className="ml-28 text-center"
            label={t('measurements.water')}
            defaultValue={data.dilutionSample2}
            labelStyleClass="w-full text-gray-700"
          />

          {/* we only show below fields on conz and viab */}
          {shouldRenderInput({ input: 'dilutionStain1', method: data.method }) ||
          shouldRenderInput({ input: 'dilutionStain2', method: data.method }) ? (
            <h2 className="mt-3 font-bold">{t('editMeasurements.dilutionStaining')}</h2>
          ) : null}

          {shouldRenderInput({ input: 'dilutionStain1', method: data.method }) && (
            <Input<ViabilityConcentrationFields>
              min="0"
              lang="de-DE"
              type="number"
              errors={errors}
              register={register}
              name="dilutionStain1"
              className="ml-28 text-center"
              defaultValue={data.dilutionStain1}
              labelStyleClass="w-full text-gray-700"
              label={t('measurements.dilutionSample')}
            />
          )}

          {shouldRenderInput({ input: 'dilutionStain2', method: data.method }) && (
            <Input<ViabilityConcentrationFields>
              min="0"
              lang="de-DE"
              type="number"
              errors={errors}
              register={register}
              name="dilutionStain2"
              className="ml-28 text-center"
              defaultValue={data.dilutionStain2}
              labelStyleClass="w-full text-gray-700"
              label={t('measurements.colorSolution')}
            />
          )}

          <div className="flex justify-end mt-6">
            <Button
              type="submit"
              onClick={handleOnSubmit}
              isLoading={updateBatchMutation.isLoading}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
